import { useMemo, useState } from "react";
import { Select, Tag } from "../ui";
import CallsPage, { setupColumn } from "../calls/CallsPage";
import { formatKigaliDate } from "../scholarships/model";
import { useAcademicYears } from "../../hooks/useAcademicYears";
import { CALL_STATES, callPhaseOf, useAbroadCalls } from "./model";
import CreateCallDialog from "./CreateCallDialog";
import CallSetupDialog from "./CallSetupDialog";

const PAGE_SIZE = 20;

// The HEC Admin's abroad loan calls: create one, then set it up in one dialog; the DG and the Minister open it read-only
export default function AbroadCallsPage() {
  const [state, setState] = useState("");
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [setupId, setSetupId] = useState(null);

  const { data, isPending, isError } = useAbroadCalls({ state, page, size: PAGE_SIZE });
  // Every year, not only the two new calls may take, so older calls keep their year's name
  const { data: years } = useAcademicYears({ includeFuture: true });
  const yearNames = useMemo(() => Object.fromEntries((years ?? []).map((y) => [y.id, y.academicYearName])), [years]);

  const columns = [
    { key: "name", label: "Loan call", render: (c) => <span className="font-medium text-slate-800">{c.name}</span> },
    { key: "academicYear", label: "Academic year", accessor: (c) => yearNames[c.academicYearId] ?? "—" },
    { key: "opensAt", label: "Opens", accessor: (c) => formatKigaliDate(c.opensAt) },
    { key: "closesAt", label: "Closes", accessor: (c) => formatKigaliDate(c.closesAt), sortValue: (c) => c.closesAt },
    { key: "programmeCount", label: "Programmes", accessor: (c) => c.programmeCount },
    {
      key: "state",
      label: "State",
      accessor: (c) => callPhaseOf(c).label,
      render: (c) => <Tag color={callPhaseOf(c).color}>{callPhaseOf(c).label}</Tag>,
    },
    setupColumn(setSetupId),
  ];

  return (
    <CallsPage
      title="Abroad loan calls"
      subtitle={data ? `${data.total} loan call${data.total === 1 ? "" : "s"}` : null}
      newLabel="New loan call"
      onNew={() => setCreating(true)}
      table={{
        columns,
        data: data?.items ?? [],
        isLoading: isPending,
        exportName: "abroad-loan-calls",
        emptyTitle: isError ? "Could not load the loan calls. Please try again later." : "No loan calls found",
        pagination: { page, totalPages: data ? Math.ceil(data.total / PAGE_SIZE) : 0, totalElements: data?.total, pageSize: PAGE_SIZE, onPageChange: setPage },
        filters: (
          <Select
            aria-label="State"
            placeholder="All states"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setPage(0);
            }}
            options={CALL_STATES}
            className="w-full sm:w-48"
          />
        ),
      }}
    >
      <CreateCallDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(call) => {
          setCreating(false);
          setSetupId(call.id);
        }}
      />
      {/* Keyed so each call starts on its first tab */}
      <CallSetupDialog key={setupId} callId={setupId} onClose={() => setSetupId(null)} />
    </CallsPage>
  );
}
